/**
 * EVI-1957: Pure helpers for requiring ≥1 Evidence chat source.
 *
 * Kept out of `sourceControlSelectors.ts` / `chatAtoms.ts` to avoid a circular
 * runtime import (selectors need value exports from chatAtoms; chatAtoms needs
 * the toggle guard).
 */
import type { SourceControlToggleState } from './sourceControlAtoms';
/** Top-level chat source buckets used by Source Control V2. */
export type ChatSourceBucket =
  | 'webSearch'
  | 'clinicalSources'
  | 'library'
  | 'webCollections'
  | 'integrationPartners';

/**
 * Enabled source buckets for chat source-control UX (EVI-1957).
 * Clinical sources count as one bucket when either guidelines or primary evidence is on.
 */
export function getEnabledChatSourceBuckets(
  state: SourceControlToggleState
): ChatSourceBucket[] {
  const buckets: ChatSourceBucket[] = [];
  if (state.webSearch) buckets.push('webSearch');
  if (state.localGuidelines || state.primaryEvidence) buckets.push('clinicalSources');
  if (state.myLibrary) buckets.push('library');
  if (state.webCollections) buckets.push('webCollections');
  if (Object.values(state.integrationPartners).some(Boolean)) {
    buckets.push('integrationPartners');
  }
  return buckets;
}

/** True when any chat source bucket is enabled. */
export function hasAnyEnabledChatSource(state: SourceControlToggleState): boolean {
  return getEnabledChatSourceBuckets(state).length > 0;
}

/** True when `bucket` is the only enabled chat source. */
export function isSoleEnabledChatSourceBucket(
  state: SourceControlToggleState,
  bucket: ChatSourceBucket
): boolean {
  const enabled = getEnabledChatSourceBuckets(state);
  return enabled.length === 1 && enabled[0] === bucket;
}

/**
 * Keep previous state when a flag-gated update would leave zero chat sources.
 */
export function applyChatSourceToggleGuard(
  prev: SourceControlToggleState,
  next: SourceControlToggleState,
  requireAtLeastOne: boolean
): SourceControlToggleState {
  if (
    requireAtLeastOne &&
    hasAnyEnabledChatSource(prev) &&
    !hasAnyEnabledChatSource(next)
  ) {
    return prev;
  }
  return next;
}
